import * as Yup from 'yup';

// UK phone number regex pattern
export const UK_PHONE_REGEX = /^(?:(?:\+44)|(?:0))(?:(?:(?:[1-9][0-9]{8})|(?:(?:[1-9][0-9]{7})|(?:[1-9][0-9]{9})|(?:[1-9][0-9]{2}\s[0-9]{4}\s[0-9]{4})|(?:[1-9][0-9]{2}\s[0-9]{3}\s[0-9]{4})))$)/;

// UK postcode regex pattern
export const UK_POSTCODE_REGEX = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;

// Business Information validation
export const businessInformationSchema = Yup.object().shape({
  businessType: Yup.string()
    .oneOf([
      'Limited Company', 
      'Sole Trader', 
      'Charity / Trust / Community Org',
      'Charity/Trust/Community Org' // Legacy format without spaces
    ], 'Invalid business type')
    .required('Business type is required'),
});

// Contact Information validation
export const contactInformationSchema = Yup.object().shape({
  companyName: Yup.string().required('Company name is required'),
  ownerName: Yup.string().required('Owner name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phoneNumber: Yup.string()
    .matches(UK_PHONE_REGEX, 'Please enter a valid UK phone number')
    .required('Phone number is required'),
  position: Yup.string().required('Position is required'),
});

// Trading Address validation
export const tradingAddressSchema = Yup.object().shape({
  tradingAddressLine1: Yup.string().required('Address line 1 is required'),
  tradingAddressLine2: Yup.string().optional(),
  tradingCity: Yup.string().required('City/Town is required'),
  tradingCounty: Yup.string().optional(),
  tradingPostcode: Yup.string()
    .matches(UK_POSTCODE_REGEX, 'Please enter a valid UK postcode')
    .required('Postcode is required'),
});

// Helper function to check if business type is charity (handles both formats)
const isCharityType = (type: string) => 
  type === 'Charity / Trust / Community Org' || type === 'Charity/Trust/Community Org';

// Registered Address validation
export const registeredAddressSchema = Yup.object().shape({
  registeredAddressLine1: Yup.string().when('businessType', {
    is: (type: string) => type === 'Limited Company' || isCharityType(type),
    then: (schema) => schema.required('Address line 1 is required'),
    otherwise: (schema) => schema.optional(),
  }),
  registeredAddressLine2: Yup.string().optional(),
  registeredCity: Yup.string().when('businessType', {
    is: (type: string) => type === 'Limited Company' || isCharityType(type),
    then: (schema) => schema.required('City/Town is required'),
    otherwise: (schema) => schema.optional(),
  }),
  registeredCounty: Yup.string().optional(),
  registeredPostcode: Yup.string().when('businessType', {
    is: (type: string) => type === 'Limited Company' || isCharityType(type),
    then: (schema) => schema.required('Postcode is required')
      .matches(UK_POSTCODE_REGEX, 'Please enter a valid UK postcode'),
    otherwise: (schema) => schema.optional(),
  }),
  companyNumber: Yup.string().when('businessType', {
    is: (type: string) => type === 'Limited Company',
    then: (schema) => schema.required('Company number is required'),
    otherwise: (schema) => schema.optional(),
  }),
  incorporationDate: Yup.string().when('businessType', {
    is: (type: string) => type === 'Limited Company',
    then: (schema) => schema.required('Incorporation date is required'),
    otherwise: (schema) => schema.optional(),
  }),
});

// Sole Trader Details validation
export const soleTraderDetailsSchema = Yup.object().shape({
  // Home Address
  homeAddressLine1: Yup.string().when('businessType', {
    is: 'Sole Trader',
    then: (schema) => schema.test('conditional-required', 'Address line 1 is required', function(value) {
      const { homeCity, homePostcode } = this.parent;
      if (homeCity || homePostcode) return !!value;
      return true;
    }),
    otherwise: (schema) => schema.optional(),
  }),
  homeAddressLine2: Yup.string().optional(),
  homeCity: Yup.string().when('businessType', {
    is: 'Sole Trader',
    then: (schema) => schema.test('conditional-required', 'City/Town is required', function(value) {
      const { homeAddressLine1, homePostcode } = this.parent;
      if (homeAddressLine1 || homePostcode) return !!value;
      return true;
    }),
    otherwise: (schema) => schema.optional(),
  }),
  homeCounty: Yup.string().optional(),
  homePostcode: Yup.string().when('businessType', {
    is: 'Sole Trader',
    then: (schema) => schema.test('conditional-required', 'Postcode is required', function(value) {
      const { homeAddressLine1, homeCity } = this.parent;
      if (homeAddressLine1 || homeCity) return !!value;
      return true;
    }).test('valid-postcode', 'Please enter a valid UK postcode', function(value) {
      if (!value) return true; // Skip validation if no postcode provided
      return UK_POSTCODE_REGEX.test(value);
    }),
    otherwise: (schema) => schema.optional(),
  }),
  
  // Date of Birth
  dobDay: Yup.string().when('businessType', {
    is: 'Sole Trader',
    then: (schema) => schema.test('conditional-required', 'Day is required', function(value) {
      const { dobMonth, dobYear } = this.parent;
      if (dobMonth || dobYear) return !!value;
      return true;
    }),
    otherwise: (schema) => schema.optional(),
  }),
  dobMonth: Yup.string().when('businessType', {
    is: 'Sole Trader',
    then: (schema) => schema.test('conditional-required', 'Month is required', function(value) {
      const { dobDay, dobYear } = this.parent;
      if (dobDay || dobYear) return !!value;
      return true;
    }),
    otherwise: (schema) => schema.optional(),
  }),
  dobYear: Yup.string().when('businessType', {
    is: 'Sole Trader',
    then: (schema) => schema.test('conditional-required', 'Year is required', function(value) {
      const { dobDay, dobMonth } = this.parent;
      if (dobDay || dobMonth) return !!value;
      return true;
    }),
    otherwise: (schema) => schema.optional(),
  }),
  
  // Trading Duration
  yearsTrading: Yup.number().transform((value) => (isNaN(value) ? undefined : value))
    .min(0, 'Years must be 0 or greater')
    .nullable()
    .optional(),
  monthsTrading: Yup.number().transform((value) => (isNaN(value) ? undefined : value))
    .min(0, 'Months must be 0 or greater')
    .max(11, 'Months must be less than 12')
    .nullable()
    .optional(),
});

// Internal Information validation
export const internalInformationSchema = Yup.object().shape({
  representativeId: Yup.string().required('Representative is required'),
  industry: Yup.string().optional(),
  partner: Yup.string().optional(),
});

// Additional Notes validation
export const additionalNotesSchema = Yup.object().shape({
  notes: Yup.string().optional(),
});

// Combined validation schema
export const clientValidationSchema = Yup.object().shape({
  ...businessInformationSchema.fields,
  ...contactInformationSchema.fields,
  ...tradingAddressSchema.fields,
  ...registeredAddressSchema.fields,
  ...soleTraderDetailsSchema.fields,
  ...internalInformationSchema.fields,
  ...additionalNotesSchema.fields,
});

export default clientValidationSchema;
